import { Head, router } from '@inertiajs/react'
import { FormEvent, useState } from 'react'
import { HiOutlineArrowLeft } from 'react-icons/hi2'
import AppLayout from '../../components/AppLayout'

type Props = {
  errors: Record<string, string[] | undefined>
}

export default function NewEvent({ errors }: Props) {
  const [processing, setProcessing] = useState(false)
  const [values, setValues] = useState({
    title: '',
    start_date: '',
    end_date: '',
    location: '',
  })
  const [banner, setBanner] = useState<File | null>(null)

  const set = (field: keyof typeof values) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => setValues({ ...values, [field]: e.target.value })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    const formData = new FormData()
    formData.append('event[title]', values.title)
    formData.append('event[start_date]', values.start_date)
    formData.append('event[end_date]', values.end_date)
    formData.append('event[location]', values.location)
    if (banner) formData.append('event[banner]', banner)

    router.post('/events', formData, {
      onFinish: () => setProcessing(false),
    })
  }

  const fieldError = (field: string) => {
    const msgs = errors[field]
    if (!msgs?.length) return null
    return <p className="mt-1 text-sm text-red-600">{msgs.join(', ')}</p>
  }

  return (
    <>
      <Head title="Create Event" />
      <AppLayout active="dashboard">
        <div className="-mt-4 mb-8 flex items-center gap-6">
          <a
            href="/dashboard"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 transition hover:bg-gray-50"
            aria-label="Back to dashboard"
          >
            <HiOutlineArrowLeft className="h-5 w-5" />
          </a>
          <h1 className="font-display text-2xl font-semibold text-gray-900">Create Event</h1>
        </div>

        <form onSubmit={handleSubmit} className="mx-auto max-w-2xl space-y-6">
          <div>
            <label htmlFor="title" className="block font-display text-sm font-medium text-gray-700">
              Event Title
            </label>
            <input
              id="title"
              type="text"
              value={values.title}
              onChange={set('title')}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink"
            />
            {fieldError('title')}
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="start_date" className="block font-display text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                id="start_date"
                type="date"
                value={values.start_date}
                onChange={set('start_date')}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink"
              />
              {fieldError('start_date')}
            </div>

            <div>
              <label htmlFor="end_date" className="block font-display text-sm font-medium text-gray-700">
                End Date
              </label>
              <input
                id="end_date"
                type="date"
                value={values.end_date}
                onChange={set('end_date')}
                required
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink"
              />
              {fieldError('end_date')}
            </div>
          </div>

          <div>
            <label htmlFor="location" className="block font-display text-sm font-medium text-gray-700">
              Location
            </label>
            <input
              id="location"
              type="text"
              value={values.location}
              onChange={set('location')}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink"
            />
            {fieldError('location')}
          </div>

          <div>
            <label htmlFor="banner" className="block font-display text-sm font-medium text-gray-700">
              Banner Image
            </label>
            <input
              id="banner"
              type="file"
              accept="image/*"
              onChange={(e) => setBanner(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-ink file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-midnight-green"
            />
          </div>

          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={processing}
              className="inline-flex items-center rounded-md bg-ink px-6 py-2.5 font-display text-sm font-medium text-white transition hover:bg-midnight-green disabled:opacity-50"
            >
              {processing ? 'Creating...' : 'Create Event'}
            </button>
            <a
              href="/dashboard"
              className="font-display text-sm font-medium text-gray-600 transition hover:text-gray-900"
            >
              Cancel
            </a>
          </div>
        </form>
      </AppLayout>
    </>
  )
}
